import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { AdpSchedule } from '@app/modules/site-settings-integrations/models/integrations.model';
import { IntegrationsService } from '@app/modules/site-settings-integrations/services/integrations.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { defer, finalize, forkJoin } from 'rxjs';
import { AdpScheduleDialogComponent } from '../adp-schedule-dialog/adp-schedule-dialog.component';
import { RowActionButtonInput, RowActionEventOutput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';

@Component({
  selector: 'app-adp-schedules',
  templateUrl: './adp-schedules.component.html',
  styleUrls: ['./adp-schedules.component.scss']
})
export class AdpSchedulesComponent implements OnInit {
  @Input() public integrationId: string;

  public columns: any[] = [
    {
      field: 'time',
      title: this.translate.instant('Adp-Schedules-Time'),
      localizationCode: 'Adp-Schedules-Time',
      dataType: 'String'
    },
    {
      field: 'timezoneDisplayName',
      title: this.translate.instant('Adp-Schedules-Timezone'),
      localizationCode: 'Adp-Schedules-Timezone',
      dataType: 'String'
    },
    {
      field: 'statusText',
      title: this.translate.instant('Status'),
      localizationCode: 'Status',
      dataType: 'String'
    }
  ];

  public gridActionButtons: RowActionButtonInput[] = [
    { id: 'enable', name: this.translate.instant('Enable'), icon: 'play_arrow' },
    { id: 'disable', name: this.translate.instant('Pause'), icon: 'pause' }
  ];

  gridDataResult: GridDataResult;
  schedules: AdpSchedule[] = [];
  isLoading: boolean = false;
  clearSelectedItems: boolean = false;

  constructor(
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private integrationService: IntegrationsService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.getData();
  }

  getData() {
    this.isLoading = true;

    this.integrationService.getAdpSchedules(this.integrationId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(res => {
        this.schedules = res;

        const data = res.map(schedule => ({
          ...schedule,
          time: `${schedule.hour.toString().padStart(2, '0')}:${schedule.minute.toString().padStart(2, '0')}`,
          statusText: schedule.isEnabled
            ? this.translate.instant('Enabled')
            : this.translate.instant('Disabled')
        }));

        this.gridDataResult = {
          data: data,
          total: data.length
        };
      });
  }

  openDialog(schedule?: AdpSchedule) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      integrationId: this.integrationId,
      schedule: schedule
    };

    const dialogRef = this.dialog.open(AdpScheduleDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(data => {
      if (data === true) {
        this.getData();
      }
    });
  }

  onRowAction(event: RowActionEventOutput) {
    const scheduleId = event.rowData.id;
    const request = event.id === 'enable'
      ? this.integrationService.enableAdpSchedule(this.integrationId, scheduleId)
      : this.integrationService.disableAdpSchedule(this.integrationId, scheduleId);

    this.overlayService.show();

    request.pipe(finalize(() => this.overlayService.hide()))
      .subscribe(() => {
        this.getData();
      });
  }

  deleteAllSelected(itemsToDelete: string[]) {
    const observables = itemsToDelete.map(id => defer(() => this.integrationService.deleteAdpSchedule(this.integrationId, id)));

    this.overlayService.show();

    forkJoin(observables)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe(() => {
        this.clearSelectedItems = !this.clearSelectedItems;
        this.getData();
        this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      });
  }
}
